import {
  isPublicWidgetConfigResponse,
  toRuntimeWidgetConfig,
  type RuntimeWidgetConfig,
} from "../config/public-config.types.ts";
import { buildPublicConfigUrl } from "./widget-api-endpoints.ts";

export type FetchPublicWidgetConfigInput = {
  apiBaseUrl: string;
  domain: string;
  fetchFn?: typeof fetch;
  projectApiKey: string;
  timeoutMs?: number;
};

export type FetchPublicWidgetConfigResult =
  | {
      config: RuntimeWidgetConfig;
      ok: true;
    }
  | {
      ok: false;
      reason:
        | "http-error"
        | "invalid-config"
        | "invalid-json"
        | "request-failed";
    };

const defaultTimeoutMs = 3000;

export async function fetchPublicWidgetConfig({
  apiBaseUrl,
  domain,
  fetchFn = fetch,
  projectApiKey,
  timeoutMs = defaultTimeoutMs,
}: FetchPublicWidgetConfigInput): Promise<FetchPublicWidgetConfigResult> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, timeoutMs);

  try {
    const response = await fetchFn(
      buildPublicConfigUrl({ apiBaseUrl, domain, projectApiKey }),
      {
        credentials: "omit",
        headers: {
          Accept: "application/json",
        },
        method: "GET",
        signal: controller.signal,
      },
    );

    if (!response.ok) {
      return { ok: false, reason: "http-error" };
    }

    let data: unknown;
    try {
      data = await response.json();
    } catch {
      return { ok: false, reason: "invalid-json" };
    }

    if (!isPublicWidgetConfigResponse(data)) {
      return { ok: false, reason: "invalid-config" };
    }

    return {
      ok: true,
      config: toRuntimeWidgetConfig(data),
    };
  } catch {
    return { ok: false, reason: "request-failed" };
  } finally {
    clearTimeout(timeoutId);
  }
}
